import { useRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import Navigation from './Navigation';
import ChangelogModal from './ChangeLogModal';
import { v4 as uuidv4 } from 'uuid';
import LicenseModal from './LicenseModal';
import { showModalDialog } from './ShowModalDialog';
import RegisterStat from './RegisterStat';
import { logStat } from './LogStat';
import ModalDialog from './ModalDialog';

const StartPage = ({ data, update, onNext, onGoTo }) => {
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [webcamStream, setWebcamStream] = useState(null);
  const [isMobile] = useState(/iPhone|Android/i.test(navigator.userAgent));
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  // Handles file selection and prepares uploaded documents
  const handleFileUpload = async (event) => {
    // Convert the FileList object from the input into a real array
    const files = Array.from(event.target.files);

    // Process each file into a document object
    const docs = await Promise.all(
      files.map(async (file) => {
        // Create a temporary preview URL (for displaying the file in the UI)
        const previewURL = URL.createObjectURL(file);

        // Return a standardized document object
        return {
          id: uuidv4(),        // Unique identifier for tracking
          file,                // The raw File object
          previewURL,          // Blob URL for previewing in <img> or <embed>
          metadata: {}         // Empty metadata, to be filled later
        };
      })
    );

    // Merge new documents with any existing ones and update state
    update({ documents: [...data.documents, ...docs] });
  };

  // Handles starting the desktop webcam capture
  const handleDesktopCameraCapture = async () => {
    try {
      // Request permission and access to the user's webcam
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });

      // Save the MediaStream object so it can be used in a <video> element
      setWebcamStream(stream);

      // Update state to show the webcam preview in the UI
      setShowWebcam(true);

    } catch (error) {
      await showModalDialog({
        title: 'Camera error',
        body: 'Camera access was denied or failed.',
        type: 'error',
        buttonType: 'ok'
      });
    }
  };

  // Effect hook: runs whenever `showWebcam` or `webcamStream` changes
  useEffect(() => {
    // Only run if webcam is supposed to be shown, 
    // the <video> element is mounted, and we have a MediaStream
    if (showWebcam && videoRef.current && webcamStream) {
      const video = videoRef.current;

      // Attach the MediaStream to the <video> element
      video.srcObject = webcamStream;

      // Start playing the live webcam feed
      video.play();
    }
    // Re-run when webcam visibility or stream changes
  }, [showWebcam, webcamStream]);

  // Captures a still image from the webcam video feed
  const handleTakePicture = () => {
    // Get references to the <video> and <canvas> elements
    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Get the 2D drawing context of the canvas
    const context = canvas.getContext('2d');

    // Match canvas dimensions to the current video frame
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame onto the canvas
    context.drawImage(video, 0, 0);

    // Convert the canvas content into a JPEG blob
    canvas.toBlob(blob => {
      // Create a File object from the blob (simulates an uploaded image file)
      const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });

      // Generate a temporary URL for previewing the captured image
      const previewURL = URL.createObjectURL(file);

      // Add the new captured image as a document to the app's state
      update({
        documents: [
          ...data.documents,
          {
            id: uuidv4(),   // Unique ID for tracking
            file,           // The captured File object
            previewURL,     // Preview URL for display
            metadata: {}    // Empty metadata, can be filled later
          }
        ]
      });

      // Stop the webcam stream and close the preview
      handleCancelWebcam();
    }, 'image/jpeg');
  };

  // Stops the webcam stream and hides the webcam preview
  const handleCancelWebcam = () => {
    // If there is an active webcam stream
    if (webcamStream) {
      // Stop all tracks (video + audio if present) to release the camera
      webcamStream.getTracks().forEach(track => track.stop());
    }

    // Hide the webcam UI
    setShowWebcam(false);

    // Clear the stored MediaStream reference
    setWebcamStream(null);
  };

  // Change handlers
  const handleTitleChange = (e) => {
    update({ title: e.target.value });
  };
  const handleDescriptionChange = (e) => {
    update({ description: e.target.value });
  };
  const handleMetadataPlacementChange = (e) => {
    update({ metadataPlacement: e.target.value });
  };

  // Next button activates if title is filled an docs are available
  const canContinue = data.title && data.documents.length > 0;

  // Import previously saved binder file
  const handleImportBinderFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const project = JSON.parse(text);

      const restoredDocs = await Promise.all(
        project.documents.map(async (doc) => {
          const blob = await fetch(doc.base64).then(res => res.blob());
          const file = new File([blob], doc.name, { type: doc.type });
          const previewURL = URL.createObjectURL(file);
          return {
            id: doc.id || uuidv4(),
            file,
            previewURL,
            metadata: doc.metadata || {}
          };
        })
      );

      update({
        title: project.title || '',
        description: project.description,
        template: project.template || [],
        metadataPlacement: project.metadataPlacement || 'cover',
        documents: restoredDocs,
        signDocument: project.signDocument || false,
        signers: project.signers || []
      });
    } catch (err) {
      await showModalDialog({
        title: 'Import Error',
        body: 'Failed to import .binder file. The file might be invalid or corrupted.',
        type: 'error',
        buttonType: 'ok'
      });
      logStat({
        endpoint: 'https://stats.soliman.nl/register',
        site: 'binder.soliman.nl',
        app: 'Binder',
        module: `Start: IMPORT ERROR`
      });
    }
  };

  return (
    <Box>
      <RegisterStat
        endpoint="https://stats.soliman.nl/register"
        site="binder.soliman.nl"
        app="Binder"
        module="Start"
      />
      <Navigation currentStepKey="start" onGoTo={onGoTo} />
      <Stack spacing={3} mt={3}>
        <TextField
          label="Document Title"
          fullWidth
          value={data.title}
          onChange={handleTitleChange}
        />

        <TextField
          label="Optional description"
          fullWidth
          value={data.description}
          onChange={handleDescriptionChange}
        />

        <FormControl fullWidth>
          <InputLabel>Metadata Placement</InputLabel>
          <Select
            value={data.metadataPlacement || 'cover'}
            label="Metadata Placement"
            onChange={handleMetadataPlacementChange}
          >
            <MenuItem value="cover">Only on cover letter</MenuItem>
            <MenuItem value="top">Also on top of each document/image</MenuItem>
          </Select>
        </FormControl>

        <Stack spacing={2} sx={{ mt: 2 }}>

          <Button variant="outlined" component="label">
            Select multiple pdf, png, jpeg's
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => handleFileUpload(e, true)}
              ref={fileInputRef}
            />
          </Button>

          {isMobile && (
            <Button variant="outlined" component="label">
              Use the camera of your device
              <input
                type="file"
                hidden
                accept="image/*"
                capture="environment"
                onChange={handleFileUpload}
                ref={cameraInputRef}
              />
            </Button>
          )}

          {!isMobile && (
            <Stack spacing={2}>
              <Button variant="outlined" onClick={handleDesktopCameraCapture}>
                Use your desktop webcam
              </Button>
              {showWebcam && (
                <Box mt={2}>
                  <video ref={videoRef} style={{ maxWidth: '100%' }} autoPlay muted playsInline></video>
                  <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
                  <Stack direction="row" spacing={2} mt={2}>
                    <Button variant="contained" onClick={handleTakePicture}>Take Picture</Button>
                    <Button variant="outlined" onClick={handleCancelWebcam}>Cancel</Button>
                  </Stack>
                </Box>
              )}
            </Stack>
          )}

          <Button variant="outlined" component="label">
            Import .binder file
            <input
              type="file"
              hidden
              accept=".binder"
              onChange={handleImportBinderFile}
            />
          </Button>

          <Box display="flex" justifyContent="center">
            <Typography variant="body2">
              {data.documents.length} document(s) selected
            </Typography>
          </Box>

          <Tooltip title={!canContinue ? 'Add a title and at least one document' : ''}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={onNext}
                disabled={!canContinue}
              >
                Next step
              </Button>
            </Stack>
          </Tooltip>

          <Button variant="text" onClick={() => setShowChangelog(true)}>
            What's New
          </Button>

          <Button
            variant="text"
            size="small"
            onClick={() => setDisclaimerOpen(true)}
            sx={{ mt: 2, alignSelf: 'center' }}
          >
            Privacy & Disclaimer
          </Button>


        </Stack>

      </Stack>

      <ChangelogModal open={showChangelog} onClose={() => setShowChangelog(false)} />
      <LicenseModal open={licenseModalOpen} onClose={() => setLicenseModalOpen(false)} />

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <a
            href="https://themarkup.org/blacklight?url=binder.soliman.nl"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Privacy Scan: Blacklight"
          >
            <img
              src="https://img.shields.io/badge/Privacy%20Scan-Blacklight-brightgreen"
              alt="Privacy Scan"
              style={{ display: 'block' }}
            />
          </a>

          <a
            href="https://developer.mozilla.org/en-US/observatory/analyze?host=binder.soliman.nl"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Security Scan: Mozilla Observatory"
          >
            <img
              src="https://img.shields.io/badge/Security%20Scan-Mozilla%20Observatory-brightgreen"
              alt="Security Scan"
              style={{ display: 'block' }}
            />
          </a>

          <a
            href="https://sitereport.netcraft.com/?url=https://binder.soliman.nl"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Backend Report: Netcraft"
          >
            <img
              src="https://img.shields.io/badge/Backend-None-success"
              alt="No Backend Badge"
              style={{ display: 'block' }}
            />
          </a>

          <a
            href="https://developer.mozilla.org/en-US/observatory/analyze?host=binder.soliman.nl"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Cookie Scan"
          >
            <img
              src="https://img.shields.io/badge/Cookies-None-brightgreen"
              alt="No Cookies Badge"
              style={{ display: 'block' }}
            />
          </a>

          <Tooltip title="View Licenses">
            <IconButton
              onClick={() => setLicenseModalOpen(true)}
              aria-label="View Licenses"
              sx={{ p: 0 }}
            >
              <img
                src="https://img.shields.io/badge/Licenses-View-informational"
                alt="Licenses Badge"
                style={{ display: 'block' }}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <ModalDialog
        open={disclaimerOpen}
        title="Privacy & Disclaimer"
        type="information"
        buttonType="ok"
        onClose={() => setDisclaimerOpen(false)}
        body={
          <>
            For improvement and bug-fixing purposes, the app logs the date, time, page, and the location in the code if an error occurs.
            Your IP address, browser details (fingerprint), and any personal data are <strong>never</strong> recorded.
            <br /><br />
            Please note: you use this app at your own risk.
            I (the creator) cannot be held responsible for any loss of data, misuse, or other issues that may occur.
          </>
        }
      />

    </Box>
  );
};

export default StartPage;
